import React, { createContext, useContext, useRef, useState } from "react";

// Define the context type
interface CameraContextType {
  videoRef: React.RefObject<HTMLVideoElement>;
  streamRef: React.RefObject<MediaStream | null>;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  isCameraEnabled: boolean;
}

// Create the context with a default value of null
const CameraContext = createContext<CameraContextType | null>(null);

export const CameraProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);

  const startCamera = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraEnabled(true);
      }
    } catch (error) {
      setIsCameraEnabled(false);
    }
  };

  const stopCamera = (): void => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraEnabled(false);
  };

  return (
    <CameraContext.Provider
      value={{ videoRef, streamRef, startCamera, stopCamera, isCameraEnabled }}
    >
      {children}
    </CameraContext.Provider>
  );
};

export const useCamera = (): CameraContextType => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error("useCamera must be used within a CameraProvider");
  }
  return context;
};
